import { TextV2, Box, Flex } from '@withjoy/joykit';
import React from 'react';
import { HotelBed } from '@withjoy/joykit/icons';
import { PlaceToStayConfig } from './PlaceToStay.config';
import { LayoutAddComponentListItem } from '@apps/admin/routes/WebsiteEditor/features/custom-page/components/LeftPanelLayouts/LayoutAddComponentListItem';
import { SimpleGrabHandle } from '@website-editor/components/SimpleGrabHandle';
import { AdminSidebarComponentProps } from '../JsonComponent.types';
import { useTranslation } from '@shared/core';
import { useGoToCreateView } from '../hooks/useJsonComponentNavigation';
import { ComponentType } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { PlaceToStay } from './PlaceToStay.types';
import { useCustomPageAccommodationData } from './hooks/useCustomPageAccommodationData';
import { usePlaceToStayTelemetry } from './PlaceToStay.telemetry';

interface NewTagProps
  extends Readonly<{
    children: React.ReactNode;
  }> {}

const NewTag: React.FC<NewTagProps> = ({ children }) => (
  <Box
    backgroundColor="newUIBrandPrimary"
    color="white"
    borderRadius={2}
    paddingX={3}
    paddingY={1}
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
  >
    {children}
  </Box>
);

// Tip:
// This is the grab handle for your component in the sidebar
// while re-ordering components.
export const PlaceToStayGrabber = (props: AdminSidebarComponentProps) => {
  const placeToStayContent: PlaceToStay = props.componentData.containerContent.content;
  const { customPageAccommodationsMap } = useCustomPageAccommodationData();
  const accommodation = placeToStayContent.accommodationPlaceId ? (customPageAccommodationsMap.get(placeToStayContent.accommodationPlaceId) ?? null) : null;

  return (
    <SimpleGrabHandle
      title={accommodation?.customName || accommodation?.name || ''}
      icon={<HotelBed size={32} />}
      {...props}
    ></SimpleGrabHandle>
  );
};

const PAGE_NAME = 'Custom page';

// Tip:
// This is the button that shows in the "Add Content" page
export const PlaceToStayAdd = (props: AdminSidebarComponentProps) => {
  const { tq } = useTranslation('placeToStayComponent');
  const { goToCreateView } = useGoToCreateView();
  const eventInfo = useEventInfo();
  const eventId = eventInfo?.eventInfo?.eventId;
  const telemetry = usePlaceToStayTelemetry();
  const handleClick = useEventCallback(() => {
    telemetry.addItemClicked(PAGE_NAME, props.pageSlug);
    const content: PlaceToStay = { accommodationPlaceId: '', sortOrder: 0 };
    goToCreateView({
      id: props.componentData.id,
      containerContent: {
        id: props.containerId,
        type: PlaceToStayConfig.componentType,
        content,
        eventId: eventId || '',
        schemaUrl: PlaceToStayConfig.schemaUrl,
      },
      type: ComponentType.jsonDataBlob,
      sortOrder: props.componentData?.sortOrder,
      jsonDataContainerId: props.containerId,
      __typename: 'JsonDataBlobComponent',
    });
  });

  return (
    <LayoutAddComponentListItem onClick={handleClick}>
      <HotelBed size={48} />
      <Flex
        flex={1}
        alignItems="center"
        columnGap={3}
      >
        <TextV2
          typographyVariant="hed2"
          textAlign="left"
        >
          {tq`Place to Stay`}
        </TextV2>
        <NewTag>
          <TextV2
            typographyVariant="label3"
            fontWeight={600}
          >
            {tq`NEW`}
          </TextV2>
        </NewTag>
      </Flex>
    </LayoutAddComponentListItem>
  );
};
